import ConfigProvider from '@roo/roo/ConfigProvider';
import ar from '@roo/roo/locale/lang/ar';
import en from '@roo/roo/locale/lang/en-US';
import zh from '@roo/roo/locale/lang/zh-CN';
import zhHK from '@roo/roo/locale/lang/zh-TW';
import rooLocale from '@roo/roo/locale';
import { i18nClient } from '@sailor/i18n-web';
import React, { useEffect, useState } from 'react';
import { getLanguage, isRTL } from '../utils';

interface RuntimeLocaleProviderProps {
  children?: React.ReactNode;
  initialLocale?: string;
}

const rooLocaleLangs = {
  'zh-HK': zhHK,
  zh,
  en,
  ar,
};

export const syncRooLocale = (runtimeLocale: string) => {
  const currentLocale = runtimeLocale || 'en';
  const rooLanguagePack = rooLocaleLangs[currentLocale as keyof typeof rooLocaleLangs] || en;
  // Roo 1.17.26 的内置语言包结构比 locale.use 的公开类型声明更深。
  rooLocale.use(rooLanguagePack as unknown as Parameters<typeof rooLocale.use>[0]);
};

export const syncDocumentLocale = (locale: string) => {
  const currentLocale = locale || 'en';
  syncRooLocale(currentLocale);
  const rtl = isRTL(currentLocale);
  document.documentElement.setAttribute('lang', currentLocale);
  document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
  return rtl;
};

const RuntimeLocaleProvider = ({
  children,
  initialLocale,
}: RuntimeLocaleProviderProps) => {
  const [currentLocale, setCurrentLocale] = useState(() => initialLocale || getLanguage());
  const rtl = isRTL(currentLocale);

  useEffect(() => {
    syncDocumentLocale(currentLocale);
  }, [currentLocale]);

  useEffect(() => {
    const handleLanguageChanged = (locale: string) => {
      const nextLocale = locale || getLanguage();
      syncDocumentLocale(nextLocale);
      setCurrentLocale(nextLocale);
    };

    i18nClient.on('languageChanged', handleLanguageChanged);
    handleLanguageChanged(getLanguage());

    return () => {
      i18nClient.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  return (
    <ConfigProvider config={{ direction: rtl ? 'RTL' : 'LTR' }}>
      {children}
    </ConfigProvider>
  );
};

export default RuntimeLocaleProvider;
